/**
 * Where a password-reset email link lands (/reset-password).
 *
 * Supabase opens a reset session from the link. With two-factor set up the
 * user enters the authenticator code first — Supabase only changes the
 * password from a session that entered one, so the email alone cannot take
 * over the account — then sets the new password and the app opens. Without
 * an authenticator yet, the password is set first and the setup step follows
 * here (the sign-in page would send a reset session straight back).
 */

import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { readAuthLinkError } from '@shared/auth/accountSecurity';
import { useAuth } from '@shared/auth/AuthContext';
import type { MfaStep } from '@shared/auth/mfa';
import { AuthCardLayout } from '../components/AuthCardLayout';
import {
  AUTH_BODY_CLASS,
  AUTH_HEADING_CLASS,
  AUTH_LINK_BUTTON_CLASS,
} from '../components/authFormStyles';
import { NewPasswordForm } from '../components/NewPasswordForm';
import { TotpChallengeStep } from '../components/TotpChallengeStep';
import { TotpEnrollStep } from '../components/TotpEnrollStep';

function InvalidResetLink() {
  return (
    <div className="w-full">
      <h2 className={AUTH_HEADING_CLASS}>Reset link expired</h2>
      <p className={AUTH_BODY_CLASS}>
        This reset link is invalid or has expired. Each link works once. Use &ldquo;Forgot
        password?&rdquo; on the sign-in page to get a new one.
      </p>
      <Link to="/" className={AUTH_LINK_BUTTON_CLASS}>
        Back to sign in
      </Link>
    </div>
  );
}

export function ResetPasswordPage() {
  const { isLoading, isAuthenticated, sessionUserId, mfaStep, isPasswordResetSession } = useAuth();
  // Read once: Supabase leaves its error in the URL of a refused link.
  const [linkError] = useState(() => readAuthLinkError(window.location));
  const [passwordSet, setPasswordSet] = useState(false);

  if (isLoading) {
    return <AuthCardLayout />;
  }

  if (linkError || !sessionUserId) {
    return (
      <AuthCardLayout>
        <InvalidResetLink />
      </AuthCardLayout>
    );
  }

  if (!isPasswordResetSession) {
    // Signed in some other way: the password is changed from Settings.
    return <Navigate to={isAuthenticated ? '/settings' : '/'} replace />;
  }

  if (passwordSet && isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <AuthCardLayout>{resetStep(mfaStep, passwordSet, () => setPasswordSet(true))}</AuthCardLayout>
  );
}

/** Code (if an authenticator is set up), then the new password, then setup (if none yet). */
function resetStep(mfaStep: MfaStep | null, passwordSet: boolean, onPasswordSet: () => void) {
  if (mfaStep === 'challenge') return <TotpChallengeStep />;
  if (!passwordSet) return <NewPasswordForm onDone={onPasswordSet} />;
  return <TotpEnrollStep />;
}
